import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import {
  useGetLogisticOperatorsPaginatedQuery,
  useGetUnscopedLogisticOperatorsPaginatedQuery,
  LogisticOperatorApi,
} from '../services/logistic-operators.service';
import type { Option } from '@/components/inputs/AutocompleteInput';

interface UseLogisticOperatorsOptionsProps {
  search?: string;
  minLength?: number;
  unscoped?: boolean;
  onlyActive?: boolean;
}

const useLogisticOperatorsOptions = ({
  search,
  minLength = 3,
  unscoped = false,
  onlyActive = false,
}: UseLogisticOperatorsOptionsProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const queryArgs = {
    paginated: { page: 1, pageSize: 100 },
    search: shouldSearch ? debouncedSearch : '',
    ...(onlyActive && { status: 'ACTIVE' }),
  };

  const scopedResult = useGetLogisticOperatorsPaginatedQuery(queryArgs, {
    skip: !shouldSearch || unscoped,
  });

  const unscopedResult = useGetUnscopedLogisticOperatorsPaginatedQuery(
    queryArgs,
    { skip: !shouldSearch || !unscoped }
  );

  const {
    data: operatorsData,
    isLoading: isLoadingOperators,
    isFetching,
  } = unscoped ? unscopedResult : scopedResult;

  const resolvedOperatorOptions: Option[] = useMemo(() => {
    if (!operatorsData?.items) return [];

    return operatorsData.items.map((operator: LogisticOperatorApi) => ({
      label: operator.businessName,
      data: operator,
    }));
  }, [operatorsData?.items]);

  const operatorOptions = useStableOptions(resolvedOperatorOptions, {
    isPending: isFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  const operators = operatorsData?.items || [];

  return {
    operators,
    operatorOptions,
    isLoadingOperators: Boolean(shouldSearch) && (isLoadingOperators || isFetching),
  };
};

export default useLogisticOperatorsOptions;
